import React, { useMemo } from "react";
import clsx from "clsx";

import { useCssVars } from "@app/hooks/use-css-vars";
import { useCardContext } from "./card-provider";

import { Icon } from "@app/components/icon";

function CardFallbackBackground() {
  return (
    <div className="absolute inset-0 rounded bg-neutrals-grey-3">
      <div className="absolute inset-0 flex items-center justify-center bg-diagonal-neutrals-grey-2">
        <p className="sh1 bg-neutrals-grey-3 p-2 text-center text-neutrals-grey-2">Background not found.</p>
      </div>
    </div>
  );
}

function CardBackgroundGradient() {
  const { useFieldValue } = useCardContext();

  const placement = useFieldValue("placement");

  const directionClass: Record<string, string> = {
    "Top Left": "bg-gradient-to-tl",
    "Top Middle": "bg-gradient-to-t",
    "Top Right": "bg-gradient-to-tr",
    "Middle Left": "bg-gradient-to-l",
    Middle: "bg-gradient-to-b",
    "Middle Right": "bg-gradient-to-r",
    "Bottom Left": "bg-gradient-to-bl",
    "Bottom Middle": "bg-gradient-to-b",
    "Bottom Right": "bg-gradient-to-br",
    "Stretch Left": "bg-gradient-to-l",
    "Stretch Middle": "bg-gradient-to-b",
    "Stretch Right": "bg-gradient-to-r",
  };

  return (
    <div
      className={clsx(
        directionClass[placement?.value ?? "Middle"] ?? directionClass["Middle"],
        "from-neutrals-black/0 via-neutrals-black/15 to-neutrals-black/60 pointer-events-none absolute inset-0 z-10",
      )}
    ></div>
  );
}

export function CardBackground() {
  const { useFieldValue } = useCardContext();

  const backgroundColor = useFieldValue("background_color");
  const backgroundAsset = useFieldValue("background_asset");

  if (backgroundAsset) {
    switch (backgroundAsset.reference.__typename) {
      case "MediaImage":
        return (
          <div className="absolute inset-0">
            <CardBackgroundGradient />
            <img
              src={backgroundAsset.reference.image.url}
              alt={backgroundAsset.reference.image.altText}
              className="block h-full w-full object-cover"
            />
          </div>
        );
      case "Video":
        return (
          <div x-data="video" className="group absolute inset-0">
            <CardBackgroundGradient />
            <button
              x-bind="toggle"
              className={clsx(
                "btn btn-icon btn-square btn-md btn-outline-dark absolute bottom-8 right-6 z-10",
                "group-ui-[bottom]/card:group-ui-[right]/card:hidden group-ui-[cta]/card:hidden group-ui-[story]/card:hidden group-ui-[stretch]/card:group-ui-[right]/card:hidden",
              )}
            >
              <Icon id="Play" className="size-4 group-ui-[playing]:hidden" />
              <Icon id="Pause" className="size-4 group-ui-[paused]:hidden" />
            </button>
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 h-full w-full object-cover"
              x-bind="source"
            >
              {backgroundAsset.reference.sources.map((source) => (
                <source key={source.url} src={source.url} />
              ))}
            </video>
          </div>
        );
    }
  }

  if (backgroundColor) {
    const vars = useCssVars({
      "--background-color": backgroundColor.reference.color.value,
    });

    return <div style={vars} className="absolute inset-0 bg-[--background-color]"></div>;
  }

  return <CardFallbackBackground />;
}
